import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useLingui } from '@lingui/react/macro';
import PhoneIcon from '~icons/lucide/phone';
import PhoneOffIcon from '~icons/lucide/phone-off';
import VideoIcon from '~icons/lucide/video';
import { useEffect, useRef, useState } from 'react';

import { RING_TIMEOUT_MS, type CallInfo } from '#features/calls/model/callStore';
import { MicDenialNote } from '#features/media/MicDenialNote';
import { useMicPermissionDenied } from '#features/media/useMicPermissionDenied';
import { Avatar } from '#features/ui/components/Avatar';
import { Button } from '#features/ui/components/Button';

/** Squircle, not a circle: the same corner language as game cards and post tiles. */
const AVATAR_RADIUS = '28px';

/**
 * The only decorative motion on the screen, and it is not decorative: the dots tick at the
 * cadence of the ringtone, so the screen and the sound agree that something is still going on.
 */
const beat = keyframes`
  0%, 60%, 100% { opacity: 0.28; }
  25% { opacity: 1; }
`;

const rise = keyframes`
  from { opacity: 0; transform: translateY(9px); }
  to { opacity: 1; transform: none; }
`;

const RingBeat = () => (
  <HStack as="span" gap="3px" aria-hidden="true">
    {[0, 1, 2].map((index) => (
      <Box
        key={index}
        as="span"
        w="3px"
        h="3px"
        borderRadius="full"
        bg="currentColor"
        opacity={0.28}
        animation={`${beat} 1.6s ease-in-out ${index * 0.16}s infinite`}
      />
    ))}
  </HStack>
);

/**
 * Hairline that drains over what is left of the ring, replacing the pulsing rings that used
 * to sit behind the avatar. Driven by a plain CSS transition rather than a keyframe loop for
 * two reasons: it has to start from wherever the ring already is (a reload mid-ring, a call
 * picked up from a resync), and a transition is wall-clock based, so it self-corrects after
 * iOS freezes the WebView in the background instead of resuming where it paused.
 */
const RingCountdown = ({ ringingSince }: { ringingSince: number | null }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [epoch, setEpoch] = useState(0);

  useEffect(() => {
    // A frozen-then-thawed tab has a stale transition in flight; re-arm it on the way back.
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setEpoch((value) => value + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  useEffect(() => {
    const element = ref.current;
    if (!element || !ringingSince) {
      return;
    }
    // `ringingSince` is server time compared against this device's clock, and the two do
    // drift (the dev node runs ~12s ahead). Clamped at both ends: unclamped, a device behind
    // the server gets scaleX > 1 and a bar that overruns the ring, and one ahead gets a
    // countdown that is already empty when the phone starts ringing.
    const elapsed = Math.min(Math.max(0, Date.now() - ringingSince), RING_TIMEOUT_MS);
    const remaining = RING_TIMEOUT_MS - elapsed;
    element.style.transition = 'none';
    element.style.transform = `scaleX(${remaining / RING_TIMEOUT_MS})`;

    // Two frames: one to commit the starting scale without a transition, one to run it down.
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        element.style.transition = `transform ${remaining}ms linear`;
        element.style.transform = 'scaleX(0)';
      });
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [ringingSince, epoch]);

  if (!ringingSince) {
    return null;
  }

  return (
    <Box
      position="absolute"
      insetX={0}
      bottom={0}
      h="2px"
      bg="whiteAlpha.200"
      zIndex={3}
      aria-hidden="true"
    >
      <Box ref={ref} h="100%" bg="fg.muted" transformOrigin="left" transform="scaleX(1)" />
    </Box>
  );
};

type RingScreenProps = {
  call: CallInfo;
  phase: 'incoming' | 'outgoing';
  peerName: string;
  onAnswer: (options?: { video: boolean }) => void;
  onHangup: () => void;
  /** Человек уже разговаривает — в голосовой комнате сессии. Полноэкранного захвата не будет. */
  compact?: boolean;
};

/**
 * Тот же звонок, но когда человек уже в разговоре: узкая карточка сверху вместо экрана целиком.
 *
 * Причина продуктовая, и её же соблюдают Discord и Telegram: пока идёт групповой разговор,
 * входящий звонок не имеет права отбирать управление им. С полноэкранным рингом человек не мог
 * ни заглушиться, ни выйти из комнаты, пока не ответит или не отклонит — то есть чужой звонок
 * распоряжался его разговором.
 *
 * Экран не остаётся пустым: под карточкой продолжает жить окно голоса, и это ровно то, что нужно
 * видеть в этот момент.
 */
const CompactRing = ({ call, phase, peerName, onAnswer, onHangup }: RingScreenProps) => {
  const { t } = useLingui();
  const incoming = phase === 'incoming';

  return (
    <Box
      position="fixed"
      top={{ base: 3, md: 4 }}
      // Центрируется отступами, а не `transform`: анимация появления `rise` доводит transform до
      // `none` и с `fill-mode: both` держит его таким навсегда — центрирование сдвигом слетало
      // сразу после появления, и карточка уезжала за правый край вместе с кнопкой «Ответить».
      left={0}
      right={0}
      mx="auto"
      zIndex="modal"
      w={{ base: 'calc(100% - 24px)', md: '420px' }}
      bg="bg.surface"
      borderWidth="1px"
      borderColor="border"
      borderRadius="2xl"
      boxShadow="0 12px 40px rgba(0, 0, 0, 0.55)"
      px={3.5}
      py={3}
      animation={`${rise} 260ms cubic-bezier(0.32, 0.72, 0, 1) both`}
    >
      <HStack gap={3}>
        <Avatar
          size="md"
          src={call.peer.avatar ?? undefined}
          name={peerName}
          borderRadius="14px"
          flexShrink={0}
        />
        <Stack gap={0} minW={0} flex={1}>
          <Text textStyle="title" lineClamp={1}>
            {peerName}
          </Text>
          <HStack gap={2} color="fg.secondary">
            <Text textStyle="caption">
              {incoming
                ? call.withVideo
                  ? t`Incoming video call`
                  : t`Incoming call`
                : t`Ringing`}
            </Text>
            <RingBeat />
          </HStack>
        </Stack>
        <HStack gap={2} flexShrink={0}>
          <Button
            h="40px"
            px={3}
            borderRadius="xl"
            bg="whiteAlpha.100"
            borderWidth="1px"
            borderColor="border"
            color="red.400"
            _hover={{ bg: 'red.500/16', borderColor: 'red.500/35' }}
            onClick={onHangup}
            aria-label={incoming ? t`Decline` : t`Cancel`}
          >
            <PhoneOffIcon />
          </Button>
          {incoming && (
            <Button
              h="40px"
              px={3}
              borderRadius="xl"
              bg="green.400"
              color="green.950"
              _hover={{ bg: 'green.300' }}
              onClick={() => onAnswer({ video: false })}
              aria-label={t`Answer`}
            >
              <PhoneIcon />
            </Button>
          )}
        </HStack>
      </HStack>
      {/* Ответ выводит из комнаты — микрофон живёт в одном разговоре. Сказать об этом надо ДО
          нажатия, а не тостом после. */}
      {incoming && (
        <Text textStyle="caption" color="fg.muted" mt={2}>
          {t`Answering leaves the session voice`}
        </Text>
      )}
      <RingCountdown ringingSince={call.ringingSince} />
    </Box>
  );
};

/**
 * Incoming / outgoing ring.
 *
 * The screen is built out of the caller: their avatar, blown up and blurred, is the
 * background, so this reads as a call from a person rather than a dialog about one. There is
 * no glow anywhere — depth comes from the blur, the scrim and a hairline inner border — and
 * the two actions are labelled buttons, because declining by accident costs a conversation.
 *
 * Bottom-anchored on mobile (thumb reach, and it clears the notch by construction), centred
 * on desktop.
 */
export const RingScreen = (props: RingScreenProps) => {
  const { call, phase, peerName, onAnswer, onHangup, compact } = props;
  const { t } = useLingui();
  const incoming = phase === 'incoming';

  /**
   * Запрещённый микрофон видно ДО того, как человек взял трубку.
   *
   * Раньше он узнавал об этом уже в разговоре: собеседник ждёт, а его не слышат. Отказ живёт
   * на устройстве, а не в звонке, и до соединения он читается ровно так же — значит незачем
   * тянуть с ответом. В сжатом виде карточки проверку не делаем: там человек уже сидит в голосе
   * сбора, то есть микрофон у него заведомо работает.
   */
  const { denied: micDenied, recheck: recheckMic } = useMicPermissionDenied(!compact);

  if (compact) {
    return <CompactRing {...props} />;
  }

  return (
    <Box
      position="fixed"
      inset={0}
      zIndex="modal"
      bg="bg"
      display="flex"
      alignItems={{ base: 'flex-end', md: 'center' }}
      justifyContent="center"
      overflow="hidden"
    >
      {/* Ambient backdrop: the peer's own avatar, out of focus. Scaled past the edges so the
          blur has pixels to sample instead of fading into transparency at the borders.
          Players without an avatar get a brand-toned wash rather than a flat black screen —
          otherwise the whole concept collapses for everyone who never uploaded a picture. */}
      <Box
        position="absolute"
        inset="-12%"
        aria-hidden="true"
        bgImage={
          call.peer.avatar
            ? `url(${call.peer.avatar})`
            : 'radial-gradient(at 30% 25%, #3d3cce 0%, transparent 62%), radial-gradient(at 72% 78%, #2b17aa 0%, transparent 58%)'
        }
        bgSize="cover"
        backgroundPosition="center"
        bgColor="brand.800"
        filter="blur(48px) saturate(1.2) brightness(0.5)"
        transform="scale(1.1)"
      />
      <Box
        position="absolute"
        inset={0}
        aria-hidden="true"
        bgGradient="to-b"
        gradientFrom="rgba(12, 13, 18, 0.55)"
        gradientVia="rgba(12, 13, 18, 0.2)"
        gradientTo="rgba(12, 13, 18, 0.96)"
      />

      <Stack
        position="relative"
        zIndex={2}
        w="full"
        maxW="360px"
        px={5}
        pb={{ base: 'calc(env(safe-area-inset-bottom, 0px) + 32px)', md: 0 }}
        gap={6}
        align="center"
      >
        <Stack gap={3.5} align="center" w="full">
          <Avatar
            size="lg"
            src={call.peer.avatar}
            name={peerName}
            w="88px"
            h="88px"
            borderRadius={AVATAR_RADIUS}
            boxShadow="inset 0 0 0 1px rgba(255, 255, 255, 0.16)"
            animation={`${rise} 420ms cubic-bezier(0.32, 0.72, 0, 1) both`}
            css={{ '--avatar-radius': AVATAR_RADIUS }}
          />
          <Stack gap={0.5} align="center" maxW="full">
            <Text
              textStyle="subtitle"
              letterSpacing="-0.02em"
              lineClamp={1}
              animation={`${rise} 420ms cubic-bezier(0.32, 0.72, 0, 1) 60ms both`}
            >
              {peerName}
            </Text>
            <HStack
              gap={2}
              color="fg.secondary"
              animation={`${rise} 420ms cubic-bezier(0.32, 0.72, 0, 1) 110ms both`}
            >
              <Text textStyle="body">
                {incoming
                  ? call.withVideo
                    ? t`Incoming video call`
                    : t`Incoming call`
                  : t`Ringing`}
              </Text>
              <RingBeat />
            </HStack>
          </Stack>
        </Stack>

        {/* Над кнопками, а не вместо них: ответить с запрещённым микрофоном — законный выбор,
            человек услышит собеседника и сможет починить доступ по ходу. */}
        {micDenied && (
          <Box w="full" borderRadius="xl" bg="whiteAlpha.50" overflow="hidden">
            <MicDenialNote onResolved={recheckMic} />
          </Box>
        )}

        <Stack
          gap={2.5}
          w="full"
          animation={`${rise} 420ms cubic-bezier(0.32, 0.72, 0, 1) 200ms both`}
        >
          {/* Video calls get a third way out. Answer-with-camera or nothing is a choice most
              people resolve by declining, which costs the call rather than the camera. */}
          {incoming && call.withVideo && (
            <Button
              variant="ghost"
              size="sm"
              h="40px"
              mb={1}
              borderRadius="xl"
              color="fg.secondary"
              // Underlined on purpose: without it a bare centred line of text above two solid
              // buttons reads as a caption, and the escape hatch goes unused.
              textDecoration="underline"
              textUnderlineOffset="3px"
              textDecorationColor="fg.subtle"
              _hover={{ bg: 'whiteAlpha.100', color: 'fg' }}
              onClick={() => onAnswer({ video: false })}
            >
              {t`Answer with voice only`}
            </Button>
          )}
          <HStack gap={2.5} w="full">
            <Button
              flex={1}
              h="50px"
              borderRadius="2xl"
              bg="whiteAlpha.100"
              borderWidth="1px"
              borderColor="border"
              color="red.400"
              _hover={{ bg: 'red.500/16', borderColor: 'red.500/35' }}
              onClick={onHangup}
            >
              <PhoneOffIcon />
              {incoming ? t`Decline` : t`Cancel`}
            </Button>
            {incoming && (
              <Button
                flex={1}
                h="50px"
                borderRadius="2xl"
                bg="green.400"
                color="green.950"
                _hover={{ bg: 'green.300' }}
                onClick={() => onAnswer()}
              >
                {call.withVideo ? <VideoIcon /> : <PhoneIcon />}
                {t`Answer`}
              </Button>
            )}
          </HStack>
        </Stack>
      </Stack>

      <RingCountdown ringingSince={call.ringingSince} />
    </Box>
  );
};
